/**
 * Platform fee rate — the client-side copy of the server's `CONFIG.PLATFORM_FEE`.
 *
 * Source: `REACT_APP_PLATFORM_FEE` in `.env` / `.env.production`, inlined at build
 * time by CRA. It MUST be kept equal to the server's value: the client computes
 * `service_fee` and `Request_Fee` with it (`sharedPricingCalculations.js`), and
 * the server bills what it stores. Changing the fee is a two-repo change plus a
 * client rebuild and deploy.
 *
 * The value is a FRACTION (0.05 = 5%), matching the server, not a percentage.
 *
 * ZERO IMPORTS BY DESIGN — loaded under Node's ESM resolver by
 * `scripts/test/platformFee.test.mjs` and, transitively, by every test that
 * imports `sharedPricingCalculations.js`.
 */

/**
 * Used ONLY when the env var is missing or malformed. Matches the server's own
 * default so the fallback is a stale-but-correct number, not a wrong one. It is
 * not the source of truth — do not tune pricing by editing it.
 */
export const DEFAULT_PLATFORM_FEE_RATE = 0.05;

let warnedFallback = false;

/**
 * Turn a raw env value into a usable rate, or null.
 * Accepts a fraction in [0, 1). Zero is legal (fee waived platform-wide).
 */
export function parsePlatformFeeRate(raw) {
  if (raw === undefined || raw === null || String(raw).trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 && n < 1 ? n : null;
}

/**
 * The rate to price with. `process.env.REACT_APP_PLATFORM_FEE` is written out
 * in full so CRA's build-time replacement finds it; do not destructure it.
 * Falls back to the default with one loud warning per page load, because a
 * silent fallback is exactly the drift this module exists to remove.
 */
export function getPlatformFeeRate() {
  const rate = parsePlatformFeeRate(process.env.REACT_APP_PLATFORM_FEE);
  if (rate !== null) return rate;
  if (!warnedFallback) {
    warnedFallback = true;
    console.warn(
      `[platformFee] REACT_APP_PLATFORM_FEE is missing or invalid — pricing with default ${DEFAULT_PLATFORM_FEE_RATE}. ` +
      'Set it in .env / .env.production to match the server\'s CONFIG.PLATFORM_FEE.'
    );
  }
  return DEFAULT_PLATFORM_FEE_RATE;
}
